import { toast } from 'vue3-toastify';
import { escapeHtml, escolherEmCards } from '@/utils/waConnectSwal';
import { MetaOnboardingDialogoAdminService } from '@/services/meta-trilha/meta-onboarding-dialogo.service';

type Desfecho = 'ABANDONADA' | 'FALHA' | 'RECUSADO' | 'CONECTADO' | 'DESCONECTADO';

/**
 * Admin encerra uma execução de onboarding à mão: escolhe o desfecho e, se o
 * número ainda espera o Conversation Routing, se isso também fica resolvido.
 * Cancela a sessão de acompanhamento por operador junto (server). Devolve
 * `true` quando encerrou.
 */
export async function encerrarExecucaoOnboarding(p: {
    idExecucao: string;
    rotulo: string;
    temOperador?: boolean;
    roteamentoPendente?: boolean;
}): Promise<boolean> {
    const desfecho = await escolherEmCards<Desfecho>({
        icone: 'ri-stop-circle-line',
        titulo: 'Encerrar esta execução?',
        descricao: `Dá a execução de <strong>${escapeHtml(p.rotulo)}</strong> por encerrada com o desfecho escolhido. Ela sai do painel ao vivo.`,
        valorInicial: 'ABANDONADA',
        opcoes: [
            { value: 'ABANDONADA', icon: 'ri-time-line', titulo: 'Abandonada', descricao: 'Ninguém vai continuar esta conexão.' },
            { value: 'RECUSADO', icon: 'ri-close-circle-line', titulo: 'Recusada pelo cliente', descricao: 'O cliente desistiu (fechou ou cancelou o diálogo da Meta).' },
            { value: 'FALHA', icon: 'ri-error-warning-line', titulo: 'Falhou', descricao: 'Parou por erro; vai ser refeita do zero.' },
            { value: 'CONECTADO', icon: 'ri-checkbox-circle-line', titulo: 'Conectada', descricao: 'A conexão está de pé (resolvida por fora).' },
        ],
        nota: p.temOperador
            ? 'Há um operador acompanhando: a sessão é cancelada e o token guardado é apagado — nada vai seguir sozinho com o padrão.'
            : 'O histórico da execução continua na trilha.',
        confirmarTexto: 'Encerrar',
        cancelarTexto: 'Voltar',
        mensagemValidacao: 'Escolha o desfecho.',
    });
    if (!desfecho) return false;

    let resolverRoteamento = false;
    if (p.roteamentoPendente) {
        const r = await escolherEmCards<'manter' | 'resolver'>({
            icone: 'ri-route-line',
            titulo: 'E o Conversation Routing?',
            descricao: 'O número ainda está marcado como "aguardando roteamento".',
            valorInicial: 'manter',
            opcoes: [
                { value: 'manter', icon: 'ri-pause-circle-line', titulo: 'Manter aguardando', descricao: 'Continua no painel até chegar a 1ª mensagem.' },
                { value: 'resolver', icon: 'ri-check-double-line', titulo: 'Dar como resolvido', descricao: 'Você confirmou com o cliente (print da aba Roteamento de conversas).' },
            ],
            confirmarTexto: 'Continuar',
            cancelarTexto: 'Voltar',
        });
        if (!r) return false;
        resolverRoteamento = r === 'resolver';
    }

    try {
        const res = await new MetaOnboardingDialogoAdminService().encerrarExecucao(p.idExecucao, { desfecho, resolverRoteamento });
        toast.success(
            `Execução encerrada (${res.desfecho})`
            + (res.sessoesCanceladas ? ' · acompanhamento cancelado' : '')
            + (res.roteamentoResolvido ? ' · roteamento resolvido' : '') + '.',
        );
        return true;
    } catch (e) {
        toast.error((e as Error)?.message || 'Não foi possível encerrar a execução.');
        return false;
    }
}

